import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, ChevronRight, Lightbulb, Check, Loader2 } from 'lucide-react';
import { useApi } from '../hooks/useApi';

interface OrientationItem {
  id: string;
  text: string;
}

interface StartResponse {
  session_id: string;
  orientation_id: string;
  current_section: string;
  section_number: number;
  total_sections: number;
  items: OrientationItem[];
}

interface RespondResponse {
  success: boolean;
  section_completed: string;
  insight: SectionInsight;
  next_section?: string;
  section_number?: number;
  total_sections?: number;
  items?: OrientationItem[];
  assessment_complete?: boolean;
}

interface CompleteResponse {
  success: boolean;
  profile: ProfileResult;
}

interface SectionInsight {
  title: string;
  content: string;
}

interface ArchetypeInfo {
  code: string;
  name: string;
  description: string;
  tracks: string[];
}

interface ProfileResult {
  archetype: {
    primary: ArchetypeInfo;
    secondary: ArchetypeInfo;
  };
  big_five: Record<string, number>;
  riasec: {
    primary: string;
    secondary: string;
    tertiary: string;
    scores: Record<string, number>;
  };
  learning_preferences: Record<string, number>;
  recommendations: {
    recommended_tracks: string[];
    learning_mode: string;
    content_style: string;
    pacing: string;
    structure: string;
  };
  validity: string;
}

const SECTION_TITLES: Record<string, string> = {
  openness: 'Curiosity & Openness',
  work_style: 'Work Style',
  social: 'Social Preferences',
  interests: 'Interests & Strengths',
  validity: 'Final Questions'
};

export default function Orientation() {
  const navigate = useNavigate();
  const { fetchApi } = useApi();

  const [phase, setPhase] = useState<'intro' | 'questions' | 'insight' | 'results'>('intro');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [currentSection, setCurrentSection] = useState<string>('openness');
  const [sectionNumber, setSectionNumber] = useState(1);
  const [items, setItems] = useState<OrientationItem[]>([]);
  const [responses, setResponses] = useState<Record<string, number>>({});
  const [insight, setInsight] = useState<SectionInsight | null>(null);
  const [profile, setProfile] = useState<ProfileResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [startTime] = useState(Date.now());

  // Start session
  const startAssessment = async () => {
    setLoading(true);
    try {
      const data = await fetchApi<StartResponse>('/api/orientation/start', {
        method: 'POST',
        body: JSON.stringify({ source: 'direct' })
      });
      setSessionId(data.session_id);
      setCurrentSection(data.current_section);
      setSectionNumber(data.section_number);
      setItems(data.items);
      setPhase('questions');
    } catch (error) {
      console.error('Failed to start:', error);
    } finally {
      setLoading(false);
    }
  };

  // Submit section responses
  const submitSection = async () => {
    if (Object.keys(responses).length < items.length) {
      alert('Please answer all questions before continuing.');
      return;
    }

    setLoading(true);
    try {
      const data = await fetchApi<RespondResponse>('/api/orientation/respond', {
        method: 'POST',
        body: JSON.stringify({
          session_id: sessionId,
          section: currentSection,
          responses
        })
      });

      setInsight(data.insight);
      setPhase('insight');

      if (data.assessment_complete) {
        // All sections done, compute profile
        setTimeout(() => completeAssessment(), 0);
      } else if (data.next_section && data.section_number && data.items) {
        // Store next section data
        setCurrentSection(data.next_section);
        setSectionNumber(data.section_number);
        setItems(data.items);
      }
    } catch (error) {
      console.error('Failed to submit:', error);
    } finally {
      setLoading(false);
    }
  };

  // Complete and get profile
  const completeAssessment = async () => {
    setLoading(true);
    try {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      const data = await fetchApi<CompleteResponse>('/api/orientation/complete', {
        method: 'POST',
        body: JSON.stringify({
          session_id: sessionId,
          time_spent_seconds: timeSpent
        })
      });
      setProfile(data.profile);
      setPhase('results');
    } catch (error) {
      console.error('Failed to complete:', error);
    } finally {
      setLoading(false);
    }
  };

  // Continue to next section after viewing insight
  const continueToNext = () => {
    if (profile) {
      setPhase('results');
    } else {
      setResponses({});
      setPhase('questions');
    }
  };

  // Handle response selection
  const selectResponse = (itemId: string, value: number) => {
    setResponses(prev => ({ ...prev, [itemId]: value }));
  };

  // Intro screen
  if (phase === 'intro') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="max-w-2xl bg-slate-800 rounded-2xl p-8 text-center">
          <div className="w-20 h-20 bg-violet-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Brain className="w-10 h-10 text-violet-400" />
          </div>

          <h1 className="text-3xl font-bold text-white mb-4">
            Discover Your Learning Profile
          </h1>

          <p className="text-slate-300 text-lg mb-6">
            This 8-10 minute assessment helps us understand how you think, learn, and what interests you.
            We'll use your answers to recommend the best tracks and learning approach for you.
          </p>

          <div className="grid grid-cols-2 gap-4 mb-8 text-left">
            <div className="bg-slate-700/50 rounded-lg p-4">
              <div className="text-violet-400 font-semibold mb-1">28 Questions</div>
              <div className="text-slate-400 text-sm">5 sections with mini-insights</div>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-4">
              <div className="text-violet-400 font-semibold mb-1">Personalized Results</div>
              <div className="text-slate-400 text-sm">Archetype + track recommendations</div>
            </div>
          </div>

          <button
            onClick={startAssessment}
            disabled={loading}
            className="bg-violet-600 hover:bg-violet-500 text-white font-semibold py-4 px-8 rounded-lg transition-colors flex items-center gap-2 mx-auto disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                Begin Assessment
                <ChevronRight className="w-5 h-5" />
              </>
            )}
          </button>

          <p className="text-slate-500 text-sm mt-6">
            Your responses are private and used only to personalize your experience.
          </p>
        </div>
      </div>
    );
  }

  // Questions screen
  if (phase === 'questions') {
    const allAnswered = Object.keys(responses).length === items.length;

    return (
      <div className="min-h-screen bg-slate-900 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Progress bar */}
          <div className="mb-8">
            <div className="flex justify-between text-sm text-slate-400 mb-2">
              <span>Section {sectionNumber} of 5</span>
              <span>{SECTION_TITLES[currentSection]}</span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-violet-500 transition-all duration-300"
                style={{ width: `${(sectionNumber / 5) * 100}%` }}
              />
            </div>
          </div>

          {/* Questions */}
          <div className="space-y-6">
            {items.map((item, index) => (
              <div key={item.id} className="bg-slate-800 rounded-xl p-6">
                <p className="text-white text-lg mb-4">
                  {index + 1}. {item.text}
                </p>

                <div className="flex justify-between gap-2">
                  {[1, 2, 3, 4, 5].map(value => (
                    <button
                      key={value}
                      onClick={() => selectResponse(item.id, value)}
                      className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                        responses[item.id] === value
                          ? 'bg-violet-600 text-white'
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                    >
                      {value === 1 && 'Strongly Disagree'}
                      {value === 2 && 'Disagree'}
                      {value === 3 && 'Neutral'}
                      {value === 4 && 'Agree'}
                      {value === 5 && 'Strongly Agree'}
                    </button>
                  ))}
                </div>

                {/* Mobile-friendly labels */}
                <div className="flex justify-between text-xs text-slate-500 mt-2 sm:hidden">
                  <span>Disagree</span>
                  <span>Agree</span>
                </div>
              </div>
            ))}
          </div>

          {/* Continue button */}
          <div className="mt-8 flex justify-end">
            <button
              onClick={submitSection}
              disabled={!allAnswered || loading}
              className="bg-violet-600 hover:bg-violet-500 disabled:bg-slate-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center gap-2"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Continue
                  <ChevronRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Insight screen
  if (phase === 'insight' && insight) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="max-w-2xl bg-slate-800 rounded-2xl p-8">
          <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mb-6">
            <Lightbulb className="w-8 h-8 text-amber-400" />
          </div>

          <h2 className="text-2xl font-bold text-white mb-4">
            {insight.title}
          </h2>

          <p className="text-slate-300 text-lg leading-relaxed mb-8">
            {insight.content}
          </p>

          <button
            onClick={continueToNext}
            className="bg-violet-600 hover:bg-violet-500 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center gap-2"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                {sectionNumber < 5 ? 'Continue to Next Section' : 'See Your Results'}
                <ChevronRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // Results screen
  if (phase === 'results' && profile) {
    return (
      <div className="min-h-screen bg-slate-900 py-8 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-10 h-10 text-emerald-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Your Learning Profile
            </h1>
            <p className="text-slate-400">
              Based on your responses, here's what we learned about you.
            </p>
          </div>

          {/* Primary Archetype */}
          <div className="bg-gradient-to-br from-violet-600/20 to-indigo-600/20 border border-violet-500/30 rounded-2xl p-8 mb-6">
            <div className="text-violet-400 text-sm font-semibold mb-2">PRIMARY ARCHETYPE</div>
            <h2 className="text-3xl font-bold text-white mb-3">
              {profile.archetype.primary.name}
            </h2>
            <p className="text-slate-300 text-lg">
              {profile.archetype.primary.description}
            </p>
          </div>

          {/* Secondary Archetype */}
          <div className="bg-slate-800 rounded-xl p-6 mb-6">
            <div className="text-slate-400 text-sm font-semibold mb-2">SECONDARY ARCHETYPE</div>
            <h3 className="text-xl font-bold text-white mb-2">
              {profile.archetype.secondary.name}
            </h3>
            <p className="text-slate-400">
              {profile.archetype.secondary.description}
            </p>
          </div>

          {/* RIASEC */}
          <div className="bg-slate-800 rounded-xl p-6 mb-6">
            <h3 className="text-lg font-semibold text-white mb-4">Interest Profile (RIASEC)</h3>
            <div className="flex flex-wrap gap-3">
              {Object.entries(profile.riasec.scores).map(([code, score]) => {
                const names: Record<string, string> = {
                  R: 'Realistic', I: 'Investigative', A: 'Artistic',
                  S: 'Social', E: 'Enterprising', C: 'Conventional'
                };
                const isPrimary = code === profile.riasec.primary;
                return (
                  <div
                    key={code}
                    className={`px-4 py-2 rounded-lg ${
                      isPrimary ? 'bg-violet-600 text-white' : 'bg-slate-700 text-slate-300'
                    }`}
                  >
                    <span className="font-semibold">{names[code]}</span>
                    <span className="ml-2 opacity-75">{score.toFixed(1)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Learning Preferences */}
          <div className="bg-slate-800 rounded-xl p-6 mb-6">
            <h3 className="text-lg font-semibold text-white mb-4">Learning Preferences</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-slate-400 text-sm">Structure</div>
                <div className="text-white font-medium">
                  {profile.recommendations.structure === 'guided' ? 'Guided Path' : 'Self-Directed'}
                </div>
              </div>
              <div>
                <div className="text-slate-400 text-sm">Social Style</div>
                <div className="text-white font-medium">
                  {profile.recommendations.learning_mode === 'cohort' ? 'With Others' : 'Solo Learner'}
                </div>
              </div>
              <div>
                <div className="text-slate-400 text-sm">Content Style</div>
                <div className="text-white font-medium">
                  {profile.recommendations.content_style === 'conceptual' ? 'Concepts First' : 'Examples First'}
                </div>
              </div>
              <div>
                <div className="text-slate-400 text-sm">Pacing</div>
                <div className="text-white font-medium">
                  {profile.recommendations.pacing === 'intensive' ? 'Focused Bursts' : 'Steady Progress'}
                </div>
              </div>
            </div>
          </div>

          {/* Recommended Tracks */}
          <div className="bg-slate-800 rounded-xl p-6 mb-8">
            <h3 className="text-lg font-semibold text-white mb-4">Recommended Tracks</h3>
            <div className="space-y-3">
              {profile.archetype.primary.tracks.map((track, i) => (
                <div
                  key={track}
                  className="flex items-center justify-between bg-slate-700/50 rounded-lg p-4"
                >
                  <span className="text-white font-medium">{track}</span>
                  {i === 0 && (
                    <span className="text-xs bg-violet-600 text-white px-2 py-1 rounded">
                      Best Match
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex-1 bg-violet-600 hover:bg-violet-500 text-white font-semibold py-4 px-6 rounded-lg transition-colors text-center"
            >
              Go to Dashboard
            </button>
            <button
              onClick={() => navigate(`/tracks/${profile.archetype.primary.tracks[0]}`)}
              className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-semibold py-4 px-6 rounded-lg transition-colors text-center"
            >
              Start Recommended Track
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading fallback
  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
    </div>
  );
}
